import { z } from "zod";

/**
 * Canonical, render-facing design specification.
 *
 * DesignBrief remains the conversational memory used by the create agent.
 * DesignSpec is the deterministic contract compiled from that memory before
 * a paid render or quote. It deliberately separates primary/accent colors and
 * preserves functional constraints that do not fit the legacy dress fields.
 */

export type DesignSpecProvenanceSource =
  | "explicit_user"
  | "selected_option"
  | "reference"
  | "inferred"
  | "legacy";

export interface DesignSpecFieldProvenance {
  source: DesignSpecProvenanceSource;
  confirmed: boolean;
}

export type DesignReferenceRole =
  | "overall"
  | "silhouette"
  | "color"
  | "fabric"
  | "neckline"
  | "sleeves"
  | "embellishment"
  | "back"
  | "construction";

export interface DesignSpecReference {
  url: string;
  side: "front" | "back" | "inspiration";
  role: DesignReferenceRole;
  instruction?: string;
}

export interface DesignSpec {
  /** Image provenance for a Back render; omitted from editable design fields. */
  renderSource?: { frontImagePath: string };
  occasion?: string;
  eventDate?: string;
  wearerContext: string[];
  visual: {
    prompt?: string;
    primaryColor?: string;
    accentColors: string[];
    length?: string;
    sleeve?: string;
    sleeveAddons: string[];
    neckline?: string;
    waistline?: string;
    silhouette?: string;
    fabric?: string;
    mood: string[];
    details: string[];
    backPrompt?: string;
    backStyle?: string;
    closure?: string;
  };
  constraints: {
    mustHave: string[];
    avoid: string[];
    comfort: string[];
    mobility: string[];
    bodyConcerns: string[];
    /** Explicit customer selection, never inferred from a reference image. */
    modesty?: "modest" | "balanced" | "revealing";
    coverage: string[];
    support: string[];
    opacity: string[];
    accessibility: string[];
    cultural: string[];
    construction: string[];
    budget: string[];
  };
  references: DesignSpecReference[];
  referenceGuidance: string[];
  provenance: Record<string, DesignSpecFieldProvenance>;
}

const designSpecTextSchema = z.string().trim().min(1).max(10_000);
const designSpecTextListSchema = z.array(designSpecTextSchema).max(100);

const designSpecFieldProvenanceSchema = z
  .object({
    source: z.enum([
      "explicit_user",
      "selected_option",
      "reference",
      "inferred",
      "legacy",
    ]),
    confirmed: z.boolean(),
  })
  .strict();

const designSpecReferenceSchema = z
  .object({
    url: z.string().trim().min(1).max(10_000),
    side: z.enum(["front", "back", "inspiration"]),
    role: z.enum([
      "overall",
      "silhouette",
      "color",
      "fabric",
      "neckline",
      "sleeves",
      "embellishment",
      "back",
      "construction",
    ]),
    instruction: designSpecTextSchema.optional(),
  })
  .strict();

/**
 * Runtime boundary for ticket persistence. The database enforces the stable
 * object shape; API routes enforce the complete DesignSpec shape.
 */
export const designSpecSchema: z.ZodType<DesignSpec, DesignSpec> = z
  .object({
    renderSource: z
      .object({ frontImagePath: z.string().trim().min(1).max(10_000) })
      .strict()
      .optional(),
    occasion: designSpecTextSchema.optional(),
    eventDate: designSpecTextSchema.optional(),
    wearerContext: designSpecTextListSchema,
    visual: z
      .object({
        prompt: designSpecTextSchema.optional(),
        primaryColor: designSpecTextSchema.optional(),
        accentColors: designSpecTextListSchema,
        length: designSpecTextSchema.optional(),
        sleeve: designSpecTextSchema.optional(),
        sleeveAddons: designSpecTextListSchema,
        neckline: designSpecTextSchema.optional(),
        waistline: designSpecTextSchema.optional(),
        silhouette: designSpecTextSchema.optional(),
        fabric: designSpecTextSchema.optional(),
        mood: designSpecTextListSchema,
        details: designSpecTextListSchema,
        backPrompt: designSpecTextSchema.optional(),
        backStyle: designSpecTextSchema.optional(),
        closure: designSpecTextSchema.optional(),
      })
      .strict(),
    constraints: z
      .object({
        mustHave: designSpecTextListSchema,
        avoid: designSpecTextListSchema,
        comfort: designSpecTextListSchema,
        mobility: designSpecTextListSchema,
        bodyConcerns: designSpecTextListSchema,
        modesty: z.enum(["modest", "balanced", "revealing"]).optional(),
        coverage: designSpecTextListSchema,
        support: designSpecTextListSchema,
        opacity: designSpecTextListSchema,
        accessibility: designSpecTextListSchema,
        cultural: designSpecTextListSchema,
        construction: designSpecTextListSchema,
        budget: designSpecTextListSchema,
      })
      .strict(),
    references: z.array(designSpecReferenceSchema).max(20),
    referenceGuidance: designSpecTextListSchema,
    provenance: z.record(z.string(), designSpecFieldProvenanceSchema),
  })
  .strict();
